import { useEffect, useState } from 'react';
import { View, Text, Pressable, ScrollView, StyleSheet, useWindowDimensions } from 'react-native';
import { X, ChevronDown, ChevronUp } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY, ELEVATION } from '../../theme';
import { CURSOR_POINTER } from '../../utils/webStyles';
import { useUIStore } from '../../store/uiStore';
import { useAuthStore } from '../../store/authStore';
import { useDefensePlansStore } from '../../store/defensePlansStore';
import { useAuthGate } from '../../hooks/useAuthGate';
import { useFieldMode } from '../../hooks/useFieldMode';
import { useConfirm } from '../common/ConfirmDialog';
import { DefensePlanFeatureRow } from './DefensePlanFeatureRow.web';
import { useRevealSelectedPlanRow } from './useRevealSelectedPlanRow.web';
import { DefensePlanBundleGroup } from './DefensePlanBundleGroup.web';
import { DefensePlanBundleRow, BUNDLE_COLLAPSE_THRESHOLD } from './DefensePlanBundleRow.web';
import { DefensePlanAssignSheet } from './DefensePlanAssignSheet.web';
import { DEFENSE_STRATEGIES } from './defenseStrategies';
import {
  summarizeAssignments,
  assignmentSummaryText,
  bundleRowWhoText,
  linksForFeature,
} from '../../utils/planAssignmentSummary';
import { groupFeaturesForList } from '../../utils/planBundles';
import { nextEventForBundle } from '../../utils/eventCalendar';
import { usePlanEventsStore } from '../../store/planEventsStore';
import { computeFeatureLabels } from '../../utils/planFeatureLabels';
import type { DefensePlan, PlanEvent } from '../../types';

const NO_EVENTS: PlanEvent[] = [];

// Same dismiss window as DefensePlanViewer.web.tsx's desktop panel — the two
// surfaces show the same notice, so they should stay up the same length of
// time.
const NOTICE_DISMISS_MS = 4000;

// Header row's fixed height, used to budget the scrollable list's max
// height against the card's own cap below.
const HEADER_HEIGHT = 52;
// Cap on the expanded card, as a fraction of the window — "compact", not a
// second map screen.
const MAX_HEIGHT_FRACTION = 0.45;

/**
 * Phone / narrow-web read-only plan surface — the DefensePlanViewer.web.tsx
 * panel has no right rail to dock to below the desktop shell, so this is a
 * floating card under the search bar instead. Collapsed it's a title
 * strip; a tap expands it to the same read-only feature rows the desktop
 * panel shows, capped short of half the screen so the map stays usable
 * underneath. Deliberately not a sheet (AdaptiveSheet or otherwise) — the
 * map has to stay interactive while this is open.
 *
 * Pinned under the search bar (`top` comes from the map screen's measured
 * search overlay): nothing else lives there, whereas the bottom of the map
 * holds the key and the control stack. It publishes its collapsed bottom
 * edge as uiStore.mapTopInset so plan fits keep features out from under it.
 * Collapsed height, not current height: the card collapses on a row tap
 * before the fit runs.
 *
 * `plan` goes null when it stops being available mid-view (deleted, or made
 * private by someone else) — see DefensePlanViewer.web.tsx's doc comment on
 * `notice`, which this card shows the same way: by itself, then closes.
 */
export function DefensePlanViewerCard({
  plan,
  notice,
  top,
}: {
  plan: DefensePlan | null;
  notice?: string | null;
  top: number;
}) {
  const { colors } = useTheme();
  const { height: windowHeight } = useWindowDimensions();
  const user = useAuthStore((s) => s.user);
  const setViewedPlanId = useUIStore((s) => s.setViewedPlanId);
  const setSelectedPlanFeature = useUIStore((s) => s.setSelectedPlanFeature);
  const requestPlanFeatureFocus = useUIStore((s) => s.requestPlanFeatureFocus);
  const selectedPlanFeatureId = useUIStore((s) => s.selectedPlanFeatureId);
  const selectedPlanBundleId = useUIStore((s) => s.selectedPlanBundleId);
  const setSelectedPlanBundle = useUIStore((s) => s.setSelectedPlanBundle);
  const requestPlanBundleFocus = useUIStore((s) => s.requestPlanBundleFocus);
  const [expanded, setExpanded] = useState(false);
  const [bundleExpandedOverrides, setBundleExpandedOverrides] = useState<Record<string, boolean>>(
    {}
  );
  const resolveBundleExpanded = (bundleId: string, memberCount: number): boolean =>
    bundleExpandedOverrides[bundleId] ?? memberCount <= BUNDLE_COLLAPSE_THRESHOLD;
  const toggleBundleExpanded = (bundleId: string, memberCount: number) => {
    setBundleExpandedOverrides((prev) => ({
      ...prev,
      [bundleId]: !resolveBundleExpanded(bundleId, memberCount),
    }));
  };
  // Shape click on the map: open the feature's bundle, scroll its row into
  // view.
  useRevealSelectedPlanRow(
    selectedPlanFeatureId,
    plan?.features,
    resolveBundleExpanded,
    (bundleId) => setBundleExpandedOverrides((prev) => ({ ...prev, [bundleId]: true }))
  );
  const setMapTopInset = useUIStore((s) => s.setMapTopInset);
  const clearViewedPlanNotice = useDefensePlansStore((s) => s.clearViewedPlanNotice);
  const partiesByPlan = useDefensePlansStore((s) => s.partiesByPlan);
  const assignmentsByPlan = useDefensePlansStore((s) => s.assignmentsByPlan);
  // Events scheduled on this plan's bundles, for the next-event line under each
  // bundle (subscribed by useDefensePlanLayer while the plan is viewed).
  const planEvents =
    usePlanEventsStore((s) => (plan ? s.eventsByPlan[plan.id] : undefined)) ?? NO_EVENTS;
  const volunteerForFeature = useDefensePlansStore((s) => s.volunteerForFeature);
  const updateAssignmentStatus = useDefensePlansStore((s) => s.updateAssignmentStatus);
  const removeAssignment = useDefensePlansStore((s) => s.removeAssignment);
  const { requireAuth } = useAuthGate();
  const confirm = useConfirm();
  // Field mode (phone website): the card only shows the plan. No assigning,
  // volunteering, marking done or stepping back.
  const readOnly = useFieldMode();
  const [assignFeatureId, setAssignFeatureId] = useState<string | null>(null);

  const parties = plan ? (partiesByPlan[plan.id] ?? {}) : {};
  const assignments = plan ? (assignmentsByPlan[plan.id] ?? {}) : {};
  const isOwner = !!(plan && user && user.id === plan.ownerId);
  const myPartyId = user
    ? (Object.values(parties).find((p) => p.uid === user.id)?.id ?? null)
    : null;

  useEffect(() => {
    setMapTopInset(top + HEADER_HEIGHT + 2);
    return () => setMapTopInset(0);
  }, [top, setMapTopInset]);

  const handleClose = () => {
    setViewedPlanId(null);
    clearViewedPlanNotice();
  };

  // Auto-dismiss the notice — see DefensePlanViewer.web.tsx's identical effect.
  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(handleClose, NOTICE_DISMISS_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notice]);

  const maxCardHeight = Math.round(windowHeight * MAX_HEIGHT_FRACTION);

  if (!plan) {
    return (
      <View
        testID="plan-viewer-card"
        style={[
          styles.wrapper,
          { top, backgroundColor: colors.surfaceElevated, borderColor: colors.border },
          ELEVATION.float,
        ]}>
        <View style={styles.header}>
          <View style={styles.headerText}>
            <Text style={[styles.title, { color: colors.text }]} numberOfLines={1}>
              Defense plan
            </Text>
            {notice && (
              <Text
                style={[styles.meta, { color: colors.textSecondary }]}
                numberOfLines={1}
                testID="plan-viewer-notice">
                {notice}
              </Text>
            )}
          </View>
          <Pressable
            onPress={handleClose}
            testID="plan-viewer-card-close"
            accessibilityRole="button"
            accessibilityLabel="Close defense plan"
            hitSlop={8}
            style={[styles.close, CURSOR_POINTER]}>
            <X size={16} color={colors.textSecondary} />
          </Pressable>
        </View>
      </View>
    );
  }

  const summary = summarizeAssignments(plan.features, assignments);
  // Tells same-strategy features apart in the list ("Fire break 1", "Fire
  // break 2", ...) — see utils/planFeatureLabels.
  const featureLabels = computeFeatureLabels(plan.features, (s) => DEFENSE_STRATEGIES[s].label);
  const Chevron = expanded ? ChevronUp : ChevronDown;

  const handleRowPress = (featureId: string) => {
    setSelectedPlanFeature(featureId);
    requestPlanFeatureFocus(featureId);
    setExpanded(false);
  };

  const handleBundleHeaderPress = (bundleId: string) => {
    setSelectedPlanBundle(bundleId);
    requestPlanBundleFocus(bundleId);
    setExpanded(false);
  };

  const handleVolunteer = (featureId: string) => {
    requireAuth(() => void volunteerForFeature(plan.id, featureId));
  };

  const handleMarkDone = (assignmentId: string) => {
    void updateAssignmentStatus(plan.id, assignmentId, 'done');
  };

  const handleStepBack = async (assignmentId: string) => {
    const ok = await confirm({
      title: 'Step back from this defense?',
      message: "You'll no longer be listed as responsible for it.",
      confirmLabel: 'Step back',
      destructive: true,
    });
    if (!ok) return;
    void removeAssignment(plan.id, assignmentId);
  };

  // The "who" column of one feature row: who is linked, plus the actions the
  // viewer may take (none in field mode).
  const whoFor = (featureId: string) => {
    const links = linksForFeature(featureId, assignments);
    const myLink = myPartyId ? (links.find((l) => l.partyId === myPartyId) ?? null) : null;
    if (readOnly) return { links, parties };
    return {
      links,
      parties,
      onAssign: isOwner ? () => setAssignFeatureId(featureId) : undefined,
      onVolunteer: !isOwner && !myLink ? () => handleVolunteer(featureId) : undefined,
      onMarkDone:
        !isOwner && myLink && myLink.status === 'accepted'
          ? () => handleMarkDone(myLink.id)
          : undefined,
      onStepBack: !isOwner && myLink ? () => void handleStepBack(myLink.id) : undefined,
    };
  };

  return (
    <View
      testID="plan-viewer-card"
      style={[
        styles.wrapper,
        { top, backgroundColor: colors.surfaceElevated, borderColor: colors.border },
        expanded && { maxHeight: maxCardHeight },
        ELEVATION.float,
      ]}>
      {/* The whole row takes a mouse click, but it isn't the button: it
          holds the chevron and close buttons, and a <button> inside a
          <button> is invalid (React warns; screen readers and keyboard
          focus misbehave). The chevron below is the keyboard and
          screen-reader toggle instead — see DefensePlanFeatureRow.web.tsx's
          identical fix. */}
      <Pressable
        onPress={() => setExpanded((v) => !v)}
        testID="plan-viewer-card-toggle"
        focusable={false}
        style={[styles.header, CURSOR_POINTER]}>
        <View style={styles.headerText}>
          <Text style={[styles.title, { color: colors.text }]} numberOfLines={1}>
            {plan.title}
          </Text>
          <Text style={[styles.meta, { color: colors.textSecondary }]} numberOfLines={1}>
            {plan.ownerName} · {assignmentSummaryText(summary)}
          </Text>
        </View>
        <Pressable
          onPress={(e) => {
            e?.stopPropagation?.();
            setExpanded((v) => !v);
          }}
          accessibilityRole="button"
          aria-expanded={expanded}
          accessibilityLabel={expanded ? 'Collapse plan' : 'Expand plan'}
          hitSlop={8}
          style={styles.chevronButton}>
          <Chevron size={16} color={colors.textTertiary} />
        </Pressable>
        <Pressable
          onPress={(e) => {
            e?.stopPropagation?.();
            handleClose();
          }}
          testID="plan-viewer-card-close"
          accessibilityRole="button"
          accessibilityLabel="Close defense plan"
          hitSlop={8}
          style={[styles.close, CURSOR_POINTER]}>
          <X size={16} color={colors.textSecondary} />
        </Pressable>
      </Pressable>
      {expanded && (
        <ScrollView
          style={{ maxHeight: maxCardHeight - HEADER_HEIGHT }}
          contentContainerStyle={styles.listContent}
          testID="plan-viewer-card-list">
          {summary.total > 0 ? (
            groupFeaturesForList(plan.features, plan.bundles ?? []).map((entry) => {
              if (entry.type === 'feature') {
                const feature = entry.feature;
                return (
                  <DefensePlanFeatureRow
                    key={feature.id}
                    feature={feature}
                    selected={selectedPlanFeatureId === feature.id}
                    onSelect={() => handleRowPress(feature.id)}
                    displayLabel={featureLabels[feature.id]}
                    who={whoFor(feature.id)}
                  />
                );
              }
              const { bundle, members } = entry;
              const bundleExpanded = resolveBundleExpanded(bundle.id, members.length);
              const bundleSelected = selectedPlanBundleId === bundle.id;
              return (
                <DefensePlanBundleGroup
                  key={bundle.id}
                  selected={bundleSelected}
                  expanded={bundleExpanded}
                  header={
                    <DefensePlanBundleRow
                      bundle={bundle}
                      members={members}
                      selected={bundleSelected}
                      expanded={bundleExpanded}
                      onToggleExpanded={() => toggleBundleExpanded(bundle.id, members.length)}
                      onSelectHeader={() => handleBundleHeaderPress(bundle.id)}
                      who={{
                        text: bundleRowWhoText(bundle, members, assignments, parties),
                      }}
                      nextEvent={nextEventForBundle(planEvents, plan?.id ?? '', bundle.id)}
                      // Shared plans only: the opportunity page refuses a private one.
                      planId={plan?.visibility === 'shared' ? plan.id : undefined}
                    />
                  }>
                  {members.map((feature) => {
                    return (
                      <DefensePlanFeatureRow
                        nested
                        key={feature.id}
                        feature={feature}
                        selected={selectedPlanFeatureId === feature.id}
                        onSelect={() => handleRowPress(feature.id)}
                        displayLabel={featureLabels[feature.id]}
                        who={whoFor(feature.id)}
                      />
                    );
                  })}
                </DefensePlanBundleGroup>
              );
            })
          ) : (
            <Text style={[styles.empty, { color: colors.textTertiary }]}>Nothing drawn yet.</Text>
          )}
        </ScrollView>
      )}

      {isOwner && !readOnly && (
        <DefensePlanAssignSheet
          visible={assignFeatureId != null}
          planId={plan.id}
          feature={plan.features.find((f) => f.id === assignFeatureId) ?? null}
          parties={parties}
          links={assignFeatureId ? linksForFeature(assignFeatureId, assignments) : []}
          areaId={plan.areaId}
          onClose={() => setAssignFeatureId(null)}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    position: 'absolute',
    left: SPACING.md,
    right: SPACING.md,
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    overflow: 'hidden',
    zIndex: 60,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    height: HEADER_HEIGHT,
    paddingHorizontal: SPACING.md,
  },
  headerText: {
    flex: 1,
  },
  title: {
    ...TYPOGRAPHY.label,
  },
  meta: {
    ...TYPOGRAPHY.caption,
    marginTop: 1,
  },
  close: {
    padding: SPACING.xxs,
  },
  chevronButton: {
    padding: SPACING.xxs,
  },
  listContent: {
    paddingHorizontal: SPACING.md,
    paddingBottom: SPACING.md,
    gap: SPACING.xs,
  },
  empty: {
    ...TYPOGRAPHY.caption,
  },
});
